import { useQueryClient } from '@tanstack/react-query'
import { useContext } from 'react'
import { isAxiosError } from 'axios'
import styled from 'styled-components'

import { MODALS_ID, QUERY_KEY } from '../../../constants'

import { abrCancelSubscriptionProduct, AbrProduct, AbrUserProduct } from '../../../utils/abr'

import { chCancelSubscriptionProduct } from '../../../utils/ch'

import { getProductDetail, getUserProductDetail, cancelProduct } from '../../../api/product'

import { ModalContext } from '../../../provider/ModalProvider'

import { useAlert } from '../../../hooks'
import { useUserInfo } from '../../../hooks/useUserInfo'

import { Button } from '../../../components/button'
import { Modal } from '../../../components/modal'
import { Text } from '../../../components/text'

const ERROR_BLACK_LIST = ['UTIL_001004', 'UTIL_001002', 'UTIL_001003', 'API_044400']

interface CancelProductConfirmModalProps {
  userProductId: number
  productId: number
  onDismiss: () => void
}

export function CancelProductConfirmModal({ userProductId, productId, onDismiss }: CancelProductConfirmModalProps) {
  const { closeModal } = useContext(ModalContext)

  const queryClient = useQueryClient()
  const { data: userInfo } = useUserInfo()
  const openAlert = useAlert()

  const close = () => closeModal(MODALS_ID.CANCEL_PRODUCT)

  const errorAlert = (message: string) => {
    openAlert({
      content: (
        <Text size={16} weight={500} lineHeight={24} $textAlign="center">
          {message}
        </Text>
      ),
    })
  }

  const cancel = async () => {
    try {
      //취소 전 Product, UserProduct 정보
      const originProduct = await getProductDetail(productId)
      const originUserProduct = await getUserProductDetail(userProductId)

      //청약 취소
      await cancelProduct(productId)

      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.product, { productId }] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.assetSummary] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.holdingProductList] })

      queryClient.setQueryData([QUERY_KEY.product, { productId }], originProduct)

      //취소 후 AirBridge 추적, 채널톡 메시지 발송
      successCancel(originProduct, originUserProduct)
    } catch (error) {
      onDismiss()
      const defaultErrorMessage = '청약 취소에 실패했습니다. 다시 시도해주세요.'
      if (isAxiosError(error)) {
        const filteredErrorList = ERROR_BLACK_LIST.includes(error.response?.data.CODE)
        const errorMessage = filteredErrorList ? error.response?.data.message : defaultErrorMessage
        errorAlert(errorMessage)
        return
      }
      errorAlert(defaultErrorMessage)
    }
  }

  const successCancel = (product: AbrProduct, userProduct: AbrUserProduct) => {
    const identification = userInfo?.identification

    abrCancelSubscriptionProduct(product, userProduct)
    chCancelSubscriptionProduct(identification, product, userProduct)

    onDismiss()
  }

  return (
    <Modal id={MODALS_ID.CANCEL_PRODUCT}>
      <Content>
        <Text size={16} weight={500} $textAlign="center" $wordBreak="keep-all">
          해당 작품을 청약 취소할까요?
        </Text>
      </Content>
      <Modal.Footer>
        <Button type="default" onClick={close}>
          아니요
        </Button>
        <Button type="primary" onClick={cancel}>
          네
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`
